import type { BookPage, SummaryGroup } from "../api/book"
import { useT } from "../lib/i18n"

export function SectionSummary({ pages, groups, current, onJump }: {
  pages: BookPage[]
  groups: SummaryGroup[]
  current: number
  onJump: (index: number) => void
}) {
  const { t } = useT()
  return (
    <aside style={{
      width: 280,
      flexShrink: 0,
      height: "100%",
      overflowY: "auto",
      borderRight: "1px solid var(--border)",
      paddingRight: 12,
    }}>
      <h3 style={{
        margin: "0 0 12px 0", fontSize: 12, color: "var(--muted)",
        textTransform: "uppercase", letterSpacing: 1,
      }}>
        {t("summary")}
      </h3>
      {groups.map((g) => {
        // Find sections (consecutive same-title pages) within this group
        const groupPages = pages.filter((p) =>
          g.kinds.includes(p.kind) &&
          (g.week_idx === undefined || p.week_idx === g.week_idx)
        )
        const sectionBreaks = breakIntoSections(groupPages)
        return (
          <section key={g.label} style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: "var(--text)",
              padding: "6px 0", borderBottom: "1px solid var(--border)",
            }}>
              {g.label}
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: "4px 0 0 0" }}>
              {sectionBreaks.map((s) => (
                <li key={s.firstIndex}>
                  <button
                    onClick={() => onJump(s.firstIndex)}
                    style={{
                      width: "100%", textAlign: "left",
                      padding: "6px 10px",
                      background: s.containsCurrent(current) ? "rgba(245,197,66,0.10)" : "transparent",
                      border: "none",
                      borderLeft: s.containsCurrent(current) ? "3px solid var(--accent)" : "3px solid transparent",
                      color: s.containsCurrent(current) ? "var(--accent)" : "var(--text)",
                      cursor: "pointer",
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: "var(--muted)", marginRight: 6 }}>p.{s.firstPageNum}</span>
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </aside>
  )
}

interface Section {
  firstIndex: number
  firstPageNum: number
  label: string
  containsCurrent: (current: number) => boolean
}

function breakIntoSections(groupPages: BookPage[]): Section[] {
  if (groupPages.length === 0) return []
  const out: Section[] = []
  let current: { startIdx: number; startPageNum: number; baseTitle: string; lastIdx: number } | null = null
  for (const p of groupPages) {
    const baseTitle = p.title.replace(/\s\(\d+\/\d+\)$/, "")
    if (current === null || current.baseTitle !== baseTitle) {
      if (current) {
        out.push(makeSection(current))
      }
      current = { startIdx: p.index, startPageNum: p.page_num, baseTitle, lastIdx: p.index }
    } else {
      current.lastIdx = p.index
    }
  }
  if (current) out.push(makeSection(current))
  return out
}

function makeSection(s: { startIdx: number; startPageNum: number; baseTitle: string; lastIdx: number }): Section {
  return {
    firstIndex: s.startIdx,
    firstPageNum: s.startPageNum,
    label: s.baseTitle,
    containsCurrent: (c) => c >= s.startIdx && c <= s.lastIdx,
  }
}
